import { subscribeBuddyUserByID } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DefaultMentorUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import type { makeRouter, RouterOptions } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html } from 'lit';
import { map, of, startWith, switchMap } from 'rxjs';
import type { MatchingConfig } from './matching-page-admin.js';

type routerRoutes = Parameters<typeof makeRouter>[0];
export const mentoringDefaultRoutes = (mc: MatchingConfig) =>
  ({
    'mentor-login': {
      path: '/login-mentor',
      render: params => {
        return html`<x-flex ${css({ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' })}>
          <x-card>
            <ma-mentor-login></ma-mentor-login>
          </x-card>
        </x-flex>`;
      },
    },
    'signup-mentor': {
      path: '/signup-mentor',
      render: stateful(params => {
        const uc = mc.mentorUserClass ?? DefaultMentorUser;
        const user = useObservable(
          () =>
            currentUser().pipe(
              switchMap(user => {
                if (!user) return of({ state: 'not_found' as const });
                const userID = user._id;
                return subscribeBuddyUserByID(
                  uc._class,
                  userID,
                )(selectionSet(() => uc, 5)).pipe(
                  map(user => {
                    if (!user) return { state: 'not_found' as const };
                    user.username = user.email; // sometimes not set for some reason
                    return { state: 'found' as const, user, userID, uc };
                  }),
                );
              }),
              startWith({ state: 'loading' as const }),
            ),
          [],
        );

        if (!user || user.state === 'loading') {
          return html`<x-loading-state></x-loading-state>`;
        }

        if (user.state === 'found' && user.user instanceof uc) {
          if (mc.configOverrides?.['signup-mentor']) {
            const form = mc.configOverrides?.['signup-mentor'].render?.({ ...params, userEntity: user.user as any });
            return html` <x-flex ${css({ width: '100%', height: '100%', overflowY: 'auto' })}> ${form} </x-flex> `;
          }
          return html`
            <x-flex ${css({ width: '100%', height: '100%', overflowY: 'auto' })}>
              <buddy-page-signup
                .userClass=${uc}
                .userEntity=${user.user}
                page-title=${clientTranslator.translate('buddy', 'mentorSignupTitle')}
                .fieldsOverride=${mc.fieldsOverride}
                .customFieldMapping=${mc.mentorCustomFieldMapping}
                ${css({ overflowY: 'visible', display: 'block', height: 'unset' })}
              ></buddy-page-signup>

              <tr-section ${css({ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' })}>
                <x-hr></x-hr>
                <x-flex horizontal end>
                  <tr-button
                    mode="text"
                    icon="logout"
                    @click=${async () => {
                      await logout();
                      goTo('/login-mentor');
                    }}
                  >
                    logout
                  </tr-button>
                </x-flex>
              </tr-section>
            </x-flex>
          `;
        }

        return html`<matching-page-admin .logo=${mc.logo}></matching-page-admin>`;
      }),
    },
  }) satisfies routerRoutes;

export const mentoringDefaultFallback = (mc: MatchingConfig) =>
  ((params: Record<string, string>) => {
    return html`<matching-page-admin .logo=${mc.logo}></matching-page-admin>`;
  }) satisfies RouterOptions['fallback'];
